'use client';

import { useEffect, useId, useState } from 'react';
import { EMAIL_SIGNUP_FORM_FIELDS } from '@/utils/contact-form';
import { submitEmailSignupForm } from '@/utils/contact-form-server';
import { validateEmail } from '@/utils/validate-email';
import clsx from 'clsx';

import { useTurnstile } from '@/components/turnstile-provider';

import Button from '../ui/button';
import Snackbar from '../ui/snackbar';
import Text from '../ui/text-input';

export type EmailSignupFormProps = {
  className?: string;
  brandKey: string;
  hideEmail?: boolean;
  hideName?: boolean;
  buttonLabel?: string;
  fnameInputLabel?: string;
  lnameInputLabel?: string;
  emailInputLabel?: string;
  header?: string;
  buttonClassName?: string;
  headerColor?: 'primary' | 'secondary' | 'tertiary';
};

const requiredMarker = (label: string, required: boolean) => {
  return required ? `${label}*` : label;
};

export const EmailSignupForm: React.FC<EmailSignupFormProps> = ({
  className,
  brandKey,
  hideEmail = false,
  hideName = false,
  buttonLabel = 'Submit',
  fnameInputLabel = EMAIL_SIGNUP_FORM_FIELDS['fname'].label,
  lnameInputLabel = EMAIL_SIGNUP_FORM_FIELDS['lname'].label,
  emailInputLabel = EMAIL_SIGNUP_FORM_FIELDS['email'].label,
  header,
  headerColor = 'primary',
  buttonClassName,
}) => {
  const defaultFormState: {
    [key: keyof typeof EMAIL_SIGNUP_FORM_FIELDS]: string;
  } = {
    fname: '',
    lname: '',
    email: '',
  };
  const formId = useId();

  const [formState, setFormState] = useState(defaultFormState);
  const [submitSuccessOpen, setSubmitSuccessOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formErrors, setFormErrors] = useState<
    Record<keyof typeof defaultFormState, string | null>
  >(
    Object.assign(
      {},
      ...Object.keys(defaultFormState).map((key) => ({ [key]: null }))
    )
  );

  const { isVerifying, hasToken, consumeToken } = useTurnstile();

  useEffect(() => {
    const isValid =
      (hideName || formState.fname.trim() !== '') &&
      validateEmail(formState.email);
    setIsFormValid(isValid);
  }, [formState, hideName]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.currentTarget.value;
    setFormState({
      ...formState,
      email,
    });
    setFormErrors({
      ...formErrors,
      email: validateEmail(email) ? null : 'Please provide a valid email.',
    });
  };

  return (
    <>
      <form
        id={'email-signup-form' + formId}
        className={clsx(
          'flex flex-col gap-[1.88rem] rounded-xl bg-neutral-light p-[1.88rem]',
          className
        )}
        onSubmit={async (e) => {
          e.preventDefault();

          if (!hasToken()) {
            console.warn('Please complete the security check');
            return;
          }

          const token = consumeToken();
          if (!token) {
            console.warn('Failed to get security token');
            return;
          }

          try {
            await submitEmailSignupForm(brandKey, token, formState);
            setFormState(defaultFormState);
            setSubmitSuccessOpen(true);
          } catch (e: any) {
            alert(
              `There was an error submitting your form. Please try again. ${e.message}`
            );
          }
        }}
      >
        {header && (
          <h6 className={`text-${headerColor}-main truncate pb-[0.25rem]`}>
            {header}
          </h6>
        )}

        {!hideName && (
          <>
            <div className="flex-1 pb-3.5 text-left">
              <Text
                id={'fname' + formId}
                label={requiredMarker(fnameInputLabel, true)}
                className="flex-initial"
                placeholder={requiredMarker(fnameInputLabel, true)}
                name="fname"
                style="white"
                required={!hideName}
                value={formState.fname}
                onChange={(e) => {
                  setFormState({
                    ...formState,
                    fname: e.currentTarget.value,
                  });
                }}
              ></Text>
            </div>
            <div className="flex-1 pb-3.5 text-left">
              <Text
                id={'lname' + formId}
                label={requiredMarker(lnameInputLabel, true)}
                className="flex-initial"
                placeholder={requiredMarker(lnameInputLabel, true)}
                name="lname"
                style="white"
                required={!hideName}
                value={formState.lname}
                onChange={(e) => {
                  setFormState({
                    ...formState,
                    lname: e.currentTarget.value,
                  });
                }}
              ></Text>
            </div>
          </>
        )}
        {!hideEmail && (
          <div className="flex-1 pb-3.5 text-left">
            <Text
              id={'email' + formId}
              label={requiredMarker(emailInputLabel, true)}
              className="flex-initial"
              placeholder={requiredMarker(emailInputLabel, true)}
              name="email"
              required={true}
              value={formState.email}
              hasError={!!formErrors.email}
              type="email"
              style="white"
              onChange={handleEmailChange}
            ></Text>
            {formErrors.email && (
              <div className="absolute mt-[0.125rem] h-3.5 text-[0.75rem] text-error-dark">
                {formErrors.email}
              </div>
            )}
          </div>
        )}

        <Button
          id={'submit-email-signup-form' + formId}
          disabled={!isFormValid || isVerifying}
          disabledCTA
          className={clsx(buttonClassName, 'h-[54px] w-full self-start')}
          title="Enter your email address to sign up"
          style="secondary"
        >
          {buttonLabel}
        </Button>
      </form>
      <Snackbar
        open={submitSuccessOpen}
        message="Thank you for your submission!"
        onClose={() => setSubmitSuccessOpen(false)}
      />
    </>
  );
};
